import { BaseApiService, ApiResponse } from './baseApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';

/** Mirrors backend BranchLutDto; extra keys tolerated from API. */
export interface BranchLutDto {
  id?: number;
  branchId?: number;
  lutNumber?: string;
  lutNo?: string;
  lutArn?: string;
  validFrom?: string;
  validTo?: string;
  [key: string]: unknown;
}

function pickFromRecord(r: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = r[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
}

/** Single-line text for invoice meta (LUT number / ARN + optional validity). */
export function formatBranchLutForInvoiceDisplay(d: BranchLutDto): string {
  const r = d as Record<string, unknown>;
  const main = pickFromRecord(r, [
    'lutNumber',
    'lutNo',
    'lut_number',
    'lutArn',
    'lut_arn',
    'arnLutNumber',
    'arn_lut_number',
    'lut',
    'number',
  ]);
  const from = pickFromRecord(r, ['validFrom', 'valid_from', 'validFromDate']);
  const to = pickFromRecord(r, ['validTo', 'valid_to', 'validToDate']);
  if (main && from && to) return `${main} (valid ${from} to ${to})`;
  if (main) return main;
  return pickFromRecord(r, ['description', 'remarks', 'note']);
}

class BranchLutApiService extends BaseApiService {
  /**
   * GET effective LUT for branch on date (defaults to today on server when date omitted).
   * Returns null for 404 / missing LUT or unsuccessful envelope (no toast).
   */
  async getEffectiveForBranchOnDate(branchId: number, date?: string): Promise<BranchLutDto | null> {
    try {
      const endpoint = API_ENDPOINTS.BRANCH_LUT.EFFECTIVE(branchId, date);
      const res = await this.makeRequest<ApiResponse<BranchLutDto>>(endpoint, { method: HTTP_METHODS.GET });
      if (res && typeof res === 'object' && 'success' in res && res.success && res.data != null) {
        return res.data as BranchLutDto;
      }
      return null;
    } catch (e: unknown) {
      const status =
        typeof e === 'object' && e !== null && 'status' in e ? (e as { status?: number }).status : undefined;
      if (status === 404) return null;
      return null;
    }
  }
}

export const branchLutApiService = new BranchLutApiService();
